<script lang="ts">import { goto } from "$app/navigation";
import { jackpotMEGA, menuAutoCollapsed, menuMobileOpen, startMegajackpotPolling } from "../state";
import { cfgToThemeCdnPath, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isMobile, toSiteUrl } from "@infinitro/global";
import { onDestroy, onMount, tick } from "svelte";
let amountBox = $state(null);
let amountText = $state(null);
let stopMegajackpotPolling;
const isMobileLayout = $derived($isMobile);
const isCollapsed = $derived($menuAutoCollapsed && !$isMobile);
const showAmount = $derived($isMobile ? $menuMobileOpen : !isCollapsed);
const shouldFitAmount = $derived(showAmount);
const logoSrc = $derived(isCollapsed ? cfgToThemeCdnPath("/sidebar/banner/jackpot-logo-collapsed.png") : cfgToThemeCdnPath("/sidebar/banner/jackpot-logo.png"));
const ariaLabel = $derived($t(Verbiage.sidebar_jackpot_banner_aria_label, "global", { amount: jackpotMEGA.value }));
const bannerLink = $derived(toSiteUrl($stateUi.sidebar.bannerLink ?? ""));
const goToBannerLink = () => {
	goto(bannerLink);
};
const onBannerKeydown = (e) => {
	if (e.key === "Enter" || e.key === " ") {
		e.preventDefault();
		goToBannerLink();
	}
};
const FIT_INSET_PX = 4;
onMount(() => {
	stopMegajackpotPolling = startMegajackpotPolling();
});
onDestroy(() => {
	stopMegajackpotPolling?.();
});
const fitAmount = () => {
	if (!amountBox || !amountText) return;
	amountText.style.fontSize = "";
	amountText.style.lineHeight = "";
	const boxWidth = amountBox.clientWidth;
	if (!boxWidth) return;
	const boxStyle = getComputedStyle(amountBox);
	const availableWidth = boxWidth - parseFloat(boxStyle.paddingLeft) - parseFloat(boxStyle.paddingRight) - FIT_INSET_PX;
	if (availableWidth <= 0) return;
	const textStyle = getComputedStyle(amountText);
	const baseFontSize = parseFloat(textStyle.fontSize);
	const baseLineHeight = parseFloat(textStyle.lineHeight);
	const textWidth = amountText.scrollWidth;
	if (!textWidth || !baseFontSize) return;
	const scale = Math.min(1, availableWidth / textWidth);
	if (scale >= 1) return;
	amountText.style.fontSize = `${baseFontSize * scale}px`;
	if (!Number.isNaN(baseLineHeight)) {
		amountText.style.lineHeight = `${baseLineHeight * scale}px`;
	}
};
const refitAmount = async () => {
	await tick();
	fitAmount();
};
$effect(() => {
	if (!shouldFitAmount || !amountBox || !amountText) return;
	jackpotMEGA.value;
	$menuMobileOpen;
	const resizeObserver = new ResizeObserver(() => {
		void refitAmount();
	});
	resizeObserver.observe(amountBox);
	void refitAmount();
	return () => resizeObserver.disconnect();
});
</script>

<a
	class="jackpot-banner"
	class:jackpot-banner-collapsed={isCollapsed}
	class:jackpot-banner-mobile={isMobileLayout}
	aria-label={ariaLabel}
	onclick={(e) => {
		e.preventDefault();
		goToBannerLink();
	}}
	onkeydown={onBannerKeydown}
>
	<div class="jackpot-banner_surface" aria-hidden="true"></div>
	<div class="jackpot-banner_glow jackpot-banner_glow--bottom" aria-hidden="true"></div>
	<div class="jackpot-banner_glow jackpot-banner_glow--top" aria-hidden="true"></div>

	<div
		class="jackpot-banner_amount-box"
		class:hidden={!showAmount}
		class:flex={showAmount}
		bind:this={amountBox}
	>
		<p class="jackpot-banner_amount" bind:this={amountText}>{jackpotMEGA.value}</p>
	</div>

	<div class="jackpot-banner_brand">
		<img
			class="jackpot-banner_logo"
			src={logoSrc}
			alt=""
			aria-hidden="true"
			onload={() => shouldFitAmount && void refitAmount()}
			onerror={(e) => {
				(e.currentTarget as HTMLImageElement).style.display = 'none';
			}}
		/>
	</div>

	<div class="jackpot-banner_inset" aria-hidden="true"></div>
</a>

<style>
	.jackpot-banner {
		position: relative;
		display: flex;
		height: 78px;
		max-height: 78px;
		min-height: 78px;
		width: 100%;
		cursor: pointer;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		overflow: hidden;
		text-decoration-line: none;
		padding: 9px 9px 0;
		border-radius: var(--border-radius-button-small);
		border: 1px solid var(--color-primary-500);
		box-shadow: 0 0 5px rgb(var(--color-primary-500-rgb) / 0.75);
}

	.jackpot-banner_surface {
		pointer-events: none;
		position: absolute;
		inset: 0px;
		border-radius: inherit;
		background: linear-gradient(
			180deg,
			var(--color-background-700) 0%,
			var(--color-background-900) 100%
		);
}

	.jackpot-banner_glow {
		pointer-events: none;
		position: absolute;
		left: -0.125rem;
		width: calc(100% + 4px);
}

	.jackpot-banner_glow--top {
		top: -1.25rem;
		height: 2.75rem;
		transform: scaleY(-1);
		background: linear-gradient(
			180deg,
			rgb(var(--color-primary-500-rgb) / 0) 0%,
			rgb(var(--color-primary-500-rgb) / 0.85) 100%
		);
}

	.jackpot-banner_glow--bottom {
		top: 3.5rem;
		height: 2.5rem;
		background: linear-gradient(
			180deg,
			rgb(var(--color-primary-500-rgb) / 0) 0%,
			rgb(var(--color-primary-500-rgb) / 0.72) 100%
		);
}

	.jackpot-banner_inset {
		pointer-events: none;
		position: absolute;
		inset: 0px;
		z-index: 2;
		border-radius: inherit;
		box-shadow: inset 0 0 5px var(--color-primary-500);
}

	.jackpot-banner_amount-box {
		position: relative;
		z-index: 1;
		display: flex;
		min-height: 0px;
		width: 100%;
		flex: 1 1 0%;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
}

	.jackpot-banner_amount {
		margin: 0px;
		display: inline-block;
		white-space: nowrap;
		text-align: center;
		font-weight: 700;
		line-height: 1;
		color: var(--color-text-100);
		font-family: var(--font-main);
		font-size: 1.6815rem;
}

	.jackpot-banner_brand {
		position: relative;
		z-index: 1;
		margin-top: auto;
		margin-bottom: 0.5rem;
		display: flex;
		height: 1.25rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 5.75rem;
}

	.jackpot-banner_logo {
		height: 100%;
		width: 100%;
		-o-object-fit: contain;
		   object-fit: contain;
}

	.jackpot-banner-mobile {
		gap: 0.125rem;
}

	.jackpot-banner-mobile .jackpot-banner_brand {
		margin-top: 0px;
		margin-bottom: 0.5rem;
		width: 5.6875rem;
}

	.jackpot-banner-collapsed {
		margin-left: auto;
		margin-right: auto;
		height: 42px;
		max-height: 42px;
		min-height: 42px;
		width: 2.625rem;
		align-items: center;
		justify-content: center;
		padding: 0px;
}

	.jackpot-banner-collapsed .jackpot-banner_amount-box {
		display: none;
}

	.jackpot-banner-collapsed .jackpot-banner_glow--top {
		top: -0.625rem;
		height: 1.25rem;
}

	.jackpot-banner-collapsed .jackpot-banner_glow--bottom {
		top: 2rem;
		height: 1.25rem;
}

	.jackpot-banner-collapsed .jackpot-banner_brand {
		margin: 0px;
		box-sizing: border-box;
		display: flex;
		height: 100%;
		width: 100%;
		flex: 1 1 0%;
		align-items: center;
		justify-content: center;
		padding-left: 2px;
		padding-right: 2px;
}

	@media (min-width: 1024px) {
		.jackpot-banner_amount {
			font-size: 2.125rem;
		}
	}
</style>
